import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { of } from "rxjs";
import { catchError, delay, map, switchMap, tap } from "rxjs/operators";

import { PATHS } from "@app/core/constants/path.const";
import { ELocale, LocaleService } from "@app/core/locale/locale.service";
import { AuthService } from "@app/core/services/auth.service";
import { LocalStorageService } from "@app/core/services/local-storage.service";
import { EToastType, ToastService } from "@app/core/services/toast.service";
import { AuthPageActions } from "@app/features/auth/store/auth.actions";
import { AccountService } from "@app/shared/services/account.service";
import { OrganisationEntityAccessUserService } from "@app/shared/services/organisation-entity-access-user.service";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { TranslateService } from "@ngx-translate/core";

@Injectable()
export class AuthEffects {
  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthPageActions.login),
      switchMap(({ request }) =>
        this.accountService.login(request).pipe(
          switchMap(response => {
            const data = response.data.signInResult;

            if (!data) {
              return of(AuthPageActions.continueToMFA({ email: request.email }));
            }

            this.authService.setAuthorizationData(data.token, data.refreshToken, data.refreshTokenExpires);

            return this.accountService.getUserDetails().pipe(
              map(response => AuthPageActions.loginSuccess({
                response: response.data,
                mustChangePassword: data.mustChangePassword,
                passwordExpiresInDays: data.passwordExpiresInDays,
              })),
              catchError(error => of(AuthPageActions.loginFailure({ errors: error.error }))),
            );
          }),
          catchError(error => of(AuthPageActions.loginFailure({ errors: error.error }))),
        ),
      ),
    ),
  );

  // ROR-5614: the API already validated the SAML assertion and issued the tokens; store them, load the
  // user, then reuse the same loginSuccess navigation as the password flow.
  ssoLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthPageActions.ssoLogin),
      switchMap(({ token, refreshToken, refreshTokenExpires }) => {
        this.authService.setAuthorizationData(token, refreshToken, refreshTokenExpires);

        return this.accountService.getUserDetails().pipe(
          map(response => AuthPageActions.loginSuccess({ response: response.data })),
          catchError(error => {
            this.authService.clearAuthorizationData();
            this.toastService.showError(this.translate.instant("AUTH.LOGIN.SSO_FAILED"));
            this.router.navigate([PATHS.auth.path, PATHS.auth.login.path]);

            return of(AuthPageActions.loginFailure({ errors: error.error }));
          }),
        );
      }),
    ),
  );

  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthPageActions.loginSuccess),
        tap(({ response, mustChangePassword }) => {
          if (response.localeCode) {
            this.onSetLocaleDate(response.localeCode);
          }

          if (mustChangePassword) {
            this.router.navigate([PATHS.forcePasswordChange.path]);
            return;
          }

          const returnUrl = this.router.routerState.snapshot.root.queryParamMap.get("page");

          if (this.isSafeInternalPath(returnUrl)) {
            this.router.navigateByUrl(returnUrl);
          } else {
            this.router.navigate([PATHS.policies.path], { queryParamsHandling: "merge" });
          }
        }),
      ),
    { dispatch: false },
  );

  getUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthPageActions.getUser),
      switchMap(() => {
        if (this.authService.getAccessToken()) {
          return this.accountService.getUserDetails().pipe(
            tap(response => {
              if (response.data.localeCode) {
                this.onSetLocaleDate(response.data.localeCode);
              }
            }),
            map(response => AuthPageActions.getUserSuccess({ response: response.data })),
            catchError(error => of(AuthPageActions.getUserFailure({ errors: error.error }))),
          );
        }

        return of(AuthPageActions.getUserNoAuth());
      }),
    ),
  );

  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthPageActions.registration),
      switchMap(({ request }) =>
        this.accountService.register(request).pipe(
          map(() => AuthPageActions.registrationSuccess()),
          catchError(error => of(AuthPageActions.registrationFailure({ errors: error.error }))),
        ),
      ),
    ),
  );

  registerSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthPageActions.registrationSuccess),
        tap(() => this.router.navigate([PATHS.auth.path, PATHS.auth.login.path])),
      ),
    { dispatch: false },
  );

  resetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthPageActions.resetPassword),
      switchMap(({ email }) => {
        return this.accountService.resetPassword(email).pipe(
          map(() => AuthPageActions.resetPasswordSuccess()),
          catchError(error => of(AuthPageActions.resetPasswordFailure({ errors: error.error }))),
        );
      }),
    ),
  );

  restorePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthPageActions.restorePassword),
      switchMap(({ request }) =>
        this.accountService.restorePassword(request).pipe(
          map(() => {
            const msg = this.translate.instant("AUTH.TOAST.RESET_SUCCESS");
            this.toastService.showSuccess(msg);
            this.router.navigate([PATHS.auth.path, PATHS.auth.login.path]);

            return AuthPageActions.restorePasswordSuccess();
          }),
          catchError(error => of(AuthPageActions.restorePasswordFailure({ errors: error.error }))),
        ),
      ),
    ),
  );

  continueToMFA$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthPageActions.continueToMFA),
        tap(({ email }) => {
          this.localStorageService.setItem("emailMFA", email);

          // Carry the guard's ?page= return url through the MFA step so loginSuccess$ can
          // return the user to their original destination (e.g. /oauth/consent) after 2FA.
          this.router.navigate([PATHS.auth.path, PATHS.auth.mfaMain.path], {
            queryParamsHandling: "merge",
          });
        }),
      ),
    { dispatch: false },
  );

  verifyMfaCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthPageActions.verifyMfaCode),
      switchMap(({ request }) =>
        this.accountService.verifyMfaEmailCode(request).pipe(
          switchMap(response => {
            const signInResult = response.data.signInResult;

            this.authService.setAuthorizationData(
              signInResult.token,
              signInResult.refreshToken,
              signInResult.refreshTokenExpires,
            );

            return this.accountService.getUserDetails().pipe(
              map(response => AuthPageActions.loginSuccess({
                response: response.data,
                mustChangePassword: signInResult.mustChangePassword,
                passwordExpiresInDays: signInResult.passwordExpiresInDays,
              })),
              catchError(error => of(AuthPageActions.verifyMfaCodeFailure({ errors: error.error }))),
            );
          }),
          catchError(error => of(AuthPageActions.verifyMfaCodeFailure({ errors: error.error }))),
        ),
      ),
    ),
  );

  resendCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthPageActions.resendCode),
      switchMap(({ request }) =>
        this.accountService.resendCode(request).pipe(
          map(() => AuthPageActions.resendCodeSuccess()),
          catchError(error => of(AuthPageActions.resendCodeFailure({ errors: error.error }))),
        ),
      ),
    ),
  );

  changeOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthPageActions.changeOrganization),
      switchMap(({ organizationId }) =>
        this.organisationUserService.changeOrganisation(organizationId).pipe(
          map(() => AuthPageActions.changeOrganizationSuccess()),
          catchError(error => of(AuthPageActions.changeOrganizationFailure({ errors: error.error }))),
        ),
      ),
    ),
  );

  changeOrganizationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthPageActions.changeOrganizationSuccess),
        tap(() => this.toastService.i10nToast(
          EToastType.Success, "Organization updated. Data now reflects the new selection.")
        ),
        delay(1200),
        tap(() => window.location.href = PATHS.policies.path)
        // Пока тестово, ну будем смотреть
      ),
    { dispatch: false },
  );

  constructor(
    private readonly router: Router,
    private readonly actions$: Actions,
    private readonly authService: AuthService,
    private readonly toastService: ToastService,
    private readonly translate: TranslateService,
    private readonly accountService: AccountService,
    private readonly localStorageService: LocalStorageService,
    private readonly localeService: LocaleService,
    private readonly organisationUserService: OrganisationEntityAccessUserService,
  ) {}

  private onSetLocaleDate(local: ELocale): void {
    this.localeService.setLocale(local);
  }

  private isSafeInternalPath(page: string | null): page is string {
    return !!page && page.startsWith("/") && !page.startsWith("//") && !page.includes("://");
  }
}
